import {
  EMPTY_SIGNUP_FORM,
  SIGNUP_STEPS,
  SignupForm,
  SignupStep,
  SignupStepProps,
  fieldStep,
} from "./signupForm";
import { FieldErrors, submitSignup } from "./submitSignup";
import { useEffect, useRef, useState } from "react";

import { AccountInfoStep } from "./steps/AccountInfoStep";
import { AuthCardLayout } from "../login/LoginCard";
import { DoneStep } from "./steps/DoneStep";
import { LandingStep } from "./steps/LandingStep";
import { NpiLookup } from "./npiLookup";
import { PasswordStep } from "./steps/PasswordStep";
import { RoleStep } from "./steps/RoleStep";
import { useRudderStackAnalytics } from "../../useAnalytics";

// One event for every step, so the funnel is one table in Metabase. Only the
// step name: never anything from the form (IOP-16).
const SIGN_UP_STEP_EVENT = "Provider Sign Up Step";

const STEP_COMPONENTS: Record<
  SignupStep,
  (props: SignupStepProps) => JSX.Element
> = {
  role: RoleStep,
  landing: LandingStep,
  account_info: AccountInfoStep,
  password: PasswordStep,
  done: DoneStep,
};

/** Provider self sign-up (VM-10039): one card that walks through the steps,
 * collecting everything into one form that is submitted at the end. */
export default function ProviderSignupPage() {
  const [step, setStep] = useState<SignupStep>("role");
  const [form, setForm] = useState<SignupForm>(EMPTY_SIGNUP_FORM);
  const [npiLookup, setNpiLookup] = useState<NpiLookup | null>(null);
  const [serverErrors, setServerErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Blocks a second submit before the disabled button has rendered.
  const inFlight = useRef(false);
  const analytics = useRudderStackAnalytics();

  // Editing a field clears the server's error on it.
  const updateForm = (changes: Partial<SignupForm>) => {
    setForm((prev) => ({ ...prev, ...changes }));
    setServerErrors((prev) => {
      const next = { ...prev };
      for (const field of Object.keys(changes) as (keyof SignupForm)[]) {
        delete next[field];
      }
      return next;
    });
  };

  const submit = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setSubmitError(null);
    const result = await submitSignup(form);
    inFlight.current = false;
    setSubmitting(false);
    if ("ok" in result) {
      // The password isn't needed once the account exists.
      setForm((prev) => ({ ...prev, password: "", passwordConfirm: "" }));
      setStep("done");
    } else if ("fieldErrors" in result) {
      setServerErrors(result.fieldErrors);
      // Back to the earliest step with an error.
      const errorSteps = (
        Object.keys(result.fieldErrors) as (keyof SignupForm)[]
      ).map(fieldStep);
      setStep(SIGNUP_STEPS.find((s) => errorSteps.includes(s)) ?? step);
    } else {
      setSubmitError(result.error);
    }
  };

  // Start each step at the top; a long step can leave the page scrolled down.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  // Reaching a step, the role choice included, so drop-off shows between them.
  // The host lets staging and preview traffic be filtered out.
  useEffect(() => {
    analytics?.track(SIGN_UP_STEP_EVENT, { step, host: window.location.host });
  }, [analytics, step]);

  const stepIndex = SIGNUP_STEPS.indexOf(step);
  const StepComponent = STEP_COMPONENTS[step];

  return (
    // The role choice matches the login card; the clinician flow needs room.
    <AuthCardLayout wide={step !== "role"} className="tw-gap-[24px]">
      <StepComponent
        form={form}
        updateForm={updateForm}
        npiLookup={npiLookup}
        setNpiLookup={setNpiLookup}
        serverErrors={serverErrors}
        submitting={submitting}
        submitError={submitError}
        // The password step is the last one before the account is created.
        onNext={
          step === "password"
            ? submit
            : () => setStep(SIGNUP_STEPS[stepIndex + 1] ?? step)
        }
        onBack={() => setStep(SIGNUP_STEPS[stepIndex - 1] ?? step)}
      />
    </AuthCardLayout>
  );
}
